import { useCallback, useEffect, useState } from "react";

// A purely visual "modern" look for the client dashboard, switched from the
// header and remembered per browser. The attribute it sets on <html> is what
// src/theme/modern.css scopes every rule under; the modern layout components
// read the same value through this hook. One module-level store so every
// subscriber flips together.
export type KitTheme = "classic" | "modern";

const STORAGE_KEY = "kit-theme";
const ATTRIBUTE = "data-kit-theme";
const FONT_LINK_ID = "kit-theme-font";
const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";

// The old look and its switch were retired 2026-10-14 (George): modern always applies, whatever an
// earlier visit stored or a ?look= link asks for.
let current: KitTheme = "modern";
const listeners = new Set<(t: KitTheme) => void>();

export const applyKitTheme = (theme: KitTheme) => {
  const root = document.documentElement;
  if (theme === "modern") {
    root.setAttribute(ATTRIBUTE, "modern");
    if (!document.getElementById(FONT_LINK_ID)) {
      const link = document.createElement("link");
      link.id = FONT_LINK_ID;
      link.rel = "stylesheet";
      link.href = FONT_HREF;
      document.head.appendChild(link);
    }
  } else {
    root.removeAttribute(ATTRIBUTE);
  }
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch (e) {
    // private mode etc. - the choice just won't persist
  }
  current = theme;
  listeners.forEach((fn) => fn(theme));
};

// Apply the stored choice as soon as the bundle loads, before first paint.
applyKitTheme(current);

export const useKitTheme = () => {
  const [theme, setTheme] = useState<KitTheme>(current);

  useEffect(() => {
    listeners.add(setTheme);
    return () => {
      listeners.delete(setTheme);
    };
  }, []);

  const toggle = useCallback(() => {
    applyKitTheme(current === "modern" ? "classic" : "modern");
  }, []);

  return { theme, toggle, isModern: theme === "modern" };
};
